import { SEARCH_ENGINES, type SearchCredentials } from '@shared/core/search';
import { z } from 'zod';

import { withRequestTimeout } from '../transport/timeout';

const SEARCH_CREDENTIALS_ENDPOINT = '/.redwood/functions/searchCredentials';
const CREDENTIALS_TIMEOUT_MS = 10_000;

const credentialsSchema = z.object({
  engine: z.enum(SEARCH_ENGINES),
  endpoint: z.string(),
  keys: z.object({ general: z.string().nullable(), orders: z.string().optional() }).nullable(),
  collections: z.array(z.string()).optional(),
  expiresAt: z.string().nullable(),
});

export class SearchCredentialsHttpError extends Error {
  constructor(readonly status: number) {
    super(`searchCredentials responded ${status}`);
    this.name = 'SearchCredentialsHttpError';
  }
}

const TRANSIENT_CLIENT_STATUSES = new Set([408, 429]);

export const isPermanentCredentialsFailure = (error: unknown): boolean =>
  error instanceof SearchCredentialsHttpError &&
  error.status >= 400 &&
  error.status < 500 &&
  !TRANSIENT_CLIENT_STATUSES.has(error.status);

/**
 * The caller's search credentials, or null when the endpoint minted no key: the member may
 * search nothing, or the Typesense minter is degraded.
 */
export async function fetchSearchCredentials(
  getToken: () => Promise<string | null>
): Promise<SearchCredentials | null> {
  const jwt = await getToken();
  const response = await fetch(SEARCH_CREDENTIALS_ENDPOINT, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'auth-provider': 'clerk',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    signal: withRequestTimeout(undefined, CREDENTIALS_TIMEOUT_MS),
  });
  if (!response.ok) throw new SearchCredentialsHttpError(response.status);
  const { keys, expiresAt, ...credentials } = credentialsSchema.parse(await response.json());
  return keys === null || expiresAt === null ? null : { ...credentials, keys, expiresAt };
}
